/**
 * RuntimeConfig — endpoint env okumalarının tek merkezi kaynağı.
 *
 * Kaynak `features/app/state/RuntimeConfig.ts` (700 satır, mobx class, çoklu-
 * tenant instance-discovery: window.__KAYNAK_BOOTSTRAP__, /.well-known/kaynak,
 * onlarca endpoint + feature/branding objesi) KÖRLEMESİNE PORT EDİLMEZ. TEBHOS
 * self-contained/tek-tenant: instance discovery, feature-flag ve branding
 * kavramları YOK. Bu dosya o class'ın MINIMAL karşılığıdır — sadece
 * {apiBase, embedBase, wsUrl} alanlarını tek yerden okuyup export eden saf
 * modül; client.ts'teki mevcut devFallback davranışı BİREBİR korunur.
 *
 * NOT (güncellendi): consumer-bağlama faz'ı tamamlandı — lib/api/client.ts
 * (API_BASE/EMBED_BASE), lib/api/ws-client.ts (WS_URL), lib/media/proxyUrl.ts,
 * AuthProvider.tsx ve add_guild_modal/GuildJoinForm.tsx artık HEPSİ bu kökten okuyor
 * (import {RuntimeConfig} from "@/lib/config/RuntimeConfig"); ayrı env okuması
 * kalmadı, tek merkezi kaynak fiilen tamamlandı.
 *
 * GATEWAY-CUTOVER (2026-07-16): `wsUrl` artık FE'nin TEK gateway uç noktası —
 * eskiden :9000 tokio (düz {type,data}) gösteriyordu, şimdi Erlang opcode-
 * gateway :9100'ü (GatewayV2Socket, tam Discord {op,t,d,s} protokolü)
 * gösterir. Ayrı bir `NEXT_PUBLIC_GATEWAY_V2_URL` env'i YOK — Kaynak'ın tek
 * `RuntimeConfig.gatewayEndpoint` kaynağı deseniyle 1-1: tek env
 * (`NEXT_PUBLIC_WS_URL`) → tek transport. localhost dev fallback eklendi
 * (eskiden GatewayV2Flag.gatewayV2Url()'de yaşıyordu — flag'le birlikte
 * silindi, fallback mantığı buraya taşındı).
 *
 * MEDYA KESİMİ (güncellendi): `apiBase` artık Kaynak `RuntimeConfig.
 * mediaEndpoint` karşılığı olarak da kullanılıyor — kullanıcı avatar/banner
 * + guild icon/banner hash-türevli CDN modeline geçti (bkz. features/user/
 * utils/AvatarUtils.ts `deriveMediaURL`); backend media-serve rotaları aynı
 * origin'de (`{apiBase}/{avatars|banners|server-icons|server-banners}/{id}/
 * {hash}.webp`). Ayrı bir `mediaBase`/resize-CDN alanı YOK — self-contained
 * tek-origin yaklaşım korunur (resize/format-negotiation Kaynak'daki gibi
 * edge'de değil, sunucu orijinal formatında servis eder).
 */

/**
 * localhost/127.0.0.1 dev fallback — env değeri yoksa yerel porta düşer
 * (lib/api/client.ts'teki `devFallback` ile BİREBİR aynı davranış).
 */
function devFallback(envVal: string | undefined, port: number, path = "/api/v1"): string {
  if (envVal) return envVal;
  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ) {
    return `http://localhost:${port}${path}`;
  }
  return "";
}

/**
 * localhost/127.0.0.1 WS dev fallback — `devFallback()` ile aynı desen ama
 * `ws://` şeması + path yok (gateway URL'i query-string ile kendi kurar,
 * bkz. GatewayV2Socket.connect()).
 */
function wsDevFallback(envVal: string | undefined, port: number): string {
  if (envVal) return envVal;
  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ) {
    return `ws://localhost:${port}`;
  }
  return "";
}

/** Endpoint konfigürasyonu (saf, modül-yükleme anında değerlenir). */
export const RuntimeConfig = {
  /** REST API tabanı (`NEXT_PUBLIC_API_URL`, dev fallback :8000/api/v1). */
  apiBase: devFallback(process.env.NEXT_PUBLIC_API_URL, 8000, "/api/v1"),
  /** Embed servisi tabanı (`NEXT_PUBLIC_EMBED_URL`, dev fallback :4001). */
  embedBase: devFallback(process.env.NEXT_PUBLIC_EMBED_URL, 4001, ""),
  /**
   * Gateway (WebSocket) uç noktası (`NEXT_PUBLIC_WS_URL`, dev fallback
   * ws://localhost:9100 — Erlang opcode-gateway). Tek kaynak: GatewayV2Socket
   * BUNDAN başka hiçbir env okumaz.
   */
  wsUrl: wsDevFallback(process.env.NEXT_PUBLIC_WS_URL, 9100),
  /** CDN tabanı (`NEXT_PUBLIC_CDN_URL`, devFallback YOK — düz env okuması, boşsa ""). */
  cdnUrl: process.env.NEXT_PUBLIC_CDN_URL || "",
  /** Site tabanı (`NEXT_PUBLIC_SITE_URL`, devFallback YOK — düz env okuması, boşsa ""). */
  siteUrl: process.env.NEXT_PUBLIC_SITE_URL || "",
  /**
   * Pazarlama/yasal-metin servisi tabanı (`NEXT_PUBLIC_MARKETING_URL`, dev
   * fallback :8774 — tebhos_marketing). Repo-yapı FİNAL (2026-07-17): kvkk/
   * terms/privacy/mesafeli-satis/iade-teslimat/cerez-politikasi/cocuk-
   * guvenligi/hesap-silme/hakkimizda sayfaları artık FE'de DEĞİL, bu serviste
   * yaşıyor (bkz. tebhos_marketing/src/routes.rs) — Footer/PartnerlikBasvuru
   * gibi FE bileşenleri bu kökten harici link üretir.
   */
  // 2026-08-09 — marketing linkleri artık HER ZAMAN aynı origin'den (göreli):
  // dev'de rspack devServer proxy'si (rspack.config.mjs), prod'da nginx
  // (deploy/nginx/nginx.conf:147) yasal yolları tebhos_marketing'e yönlendirir.
  // Eski localhost:8774 fallback'i kullanıcıyı farklı origin'e sıçratıyordu.
  marketingBase: process.env.NEXT_PUBLIC_MARKETING_URL || "",
  /**
   * Captcha sağlayıcısı — Kaynak `RuntimeConfig.captchaProvider` karşılığı.
   * Kaynak'da instance-discovery'den gelir; TEBHOS tek-tenant → sabit
   * 'hcaptcha' (CaptchaModal turnstile dalını yalnız turnstileSiteKey
   * set edilirse kullanır).
   */
  captchaProvider: "hcaptcha" as "hcaptcha" | "turnstile",
  /** hCaptcha site anahtarı (`NEXT_PUBLIC_HCAPTCHA_SITE_KEY`) — Kaynak `hcaptchaSiteKey`. */
  hcaptchaSiteKey: process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY || null,
  /** Turnstile site anahtarı (`NEXT_PUBLIC_TURNSTILE_SITE_KEY`) — Kaynak `turnstileSiteKey`; env kalkınca null. */
  turnstileSiteKey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || null,
} as const;

export type RuntimeConfigShape = typeof RuntimeConfig;
